import toast from 'react-hot-toast';

export const PLAN_LABELS = { trial: 'Trial', starter: 'Starter', growth: 'Growth', agency: 'Enterprise', custom: 'Custom' };

// Internal plan names (e.g. 'agency') must never reach a customer's screen — always show this instead.
export const planLabel = (name) => PLAN_LABELS[name] || (name ? String(name).charAt(0).toUpperCase() + String(name).slice(1) : '');

export const CURRENCIES = [
  { code: 'INR', symbol: '₹',    locale: 'en-IN', name: 'Indian Rupee (₹)' },
  { code: 'USD', symbol: '$',    locale: 'en-US', name: 'US Dollar ($)' },
  { code: 'EUR', symbol: '€',    locale: 'en-EU', name: 'Euro (€)' },
  { code: 'GBP', symbol: '£',    locale: 'en-GB', name: 'British Pound (£)' },
  { code: 'AED', symbol: 'AED',  locale: 'en-AE', name: 'UAE Dirham (AED)' },
  { code: 'SAR', symbol: 'SAR',  locale: 'en-SA', name: 'Saudi Riyal (SAR)' },
  { code: 'QAR', symbol: 'QAR',  locale: 'en-QA', name: 'Qatari Riyal (QAR)' },
  { code: 'KWD', symbol: 'KWD',  locale: 'en-KW', name: 'Kuwaiti Dinar (KWD)' },
  { code: 'BHD', symbol: 'BHD',  locale: 'en-BH', name: 'Bahraini Dinar (BHD)' },
  { code: 'OMR', symbol: 'OMR',  locale: 'en-OM', name: 'Omani Rial (OMR)' },
  { code: 'SGD', symbol: 'S$',   locale: 'en-SG', name: 'Singapore Dollar (S$)' },
  { code: 'AUD', symbol: 'A$',   locale: 'en-AU', name: 'Australian Dollar (A$)' },
  { code: 'CAD', symbol: 'C$',   locale: 'en-CA', name: 'Canadian Dollar (C$)' },
  { code: 'MYR', symbol: 'RM',   locale: 'ms-MY', name: 'Malaysian Ringgit (RM)' },
  { code: 'JPY', symbol: '¥',    locale: 'ja-JP', name: 'Japanese Yen (¥)' },
  { code: 'CNY', symbol: '¥',    locale: 'zh-CN', name: 'Chinese Yuan (¥)' },
  { code: 'CHF', symbol: 'CHF',  locale: 'de-CH', name: 'Swiss Franc (CHF)' },
  { code: 'NZD', symbol: 'NZ$',  locale: 'en-NZ', name: 'New Zealand Dollar (NZ$)' },
  { code: 'ZAR', symbol: 'R',    locale: 'en-ZA', name: 'South African Rand (R)' },
  { code: 'PKR', symbol: '₨',    locale: 'ur-PK', name: 'Pakistani Rupee (₨)' },
  { code: 'BDT', symbol: '৳',    locale: 'bn-BD', name: 'Bangladeshi Taka (৳)' },
  { code: 'LKR', symbol: 'Rs',   locale: 'si-LK', name: 'Sri Lankan Rupee (Rs)' },
  { code: 'NPR', symbol: 'Rs',   locale: 'ne-NP', name: 'Nepalese Rupee (Rs)' },
  { code: 'THB', symbol: '฿',    locale: 'th-TH', name: 'Thai Baht (฿)' },
  { code: 'IDR', symbol: 'Rp',   locale: 'id-ID', name: 'Indonesian Rupiah (Rp)' },
  { code: 'PHP', symbol: '₱',    locale: 'fil-PH', name: 'Philippine Peso (₱)' },
  { code: 'NGN', symbol: '₦',    locale: 'en-NG', name: 'Nigerian Naira (₦)' },
  { code: 'KES', symbol: 'KSh',  locale: 'en-KE', name: 'Kenyan Shilling (KSh)' },
  { code: 'MXN', symbol: '$',    locale: 'es-MX', name: 'Mexican Peso ($)' },
  { code: 'BRL', symbol: 'R$',   locale: 'pt-BR', name: 'Brazilian Real (R$)' },
];

// Used for the Company State (Settings) and Place of Supply (Invoices/Quotations)
// dropdowns — comparing these two decides CGST+SGST (same state) vs IGST (different state).
export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

// Mirrors server/src/utils/helpers.js computeDocTotals — used to live-preview
// invoice/quotation totals in the create/edit forms before submitting.
export const computeDocTotals = ({ items = [], discountType, discountValue, gstPercent = 18, companyState, placeOfSupply }) => {
  const subtotal = items.reduce((sum, i) => sum + parseFloat(i.totalPrice || 0), 0);
  const gst = parseFloat(gstPercent) || 0;
  const dVal = parseFloat(discountValue || 0);

  let discountAmount = 0;
  if (discountType === 'percent' && dVal > 0) discountAmount = (subtotal * dVal) / 100;
  else if (discountType === 'flat' && dVal > 0) discountAmount = dVal;
  discountAmount = Math.min(Math.max(discountAmount, 0), subtotal);
  const discountPercent = subtotal > 0 ? (discountAmount / subtotal) * 100 : 0;

  const taxableAmount = subtotal - discountAmount;
  const isInterState = !!(companyState && placeOfSupply && companyState.trim().toLowerCase() !== placeOfSupply.trim().toLowerCase());

  let cgstPercent = 0, sgstPercent = 0, igstPercent = 0;
  let cgstAmount = 0, sgstAmount = 0, igstAmount = 0;
  if (isInterState) {
    igstPercent = gst;
    igstAmount = (taxableAmount * gst) / 100;
  } else {
    cgstPercent = gst / 2;
    sgstPercent = gst / 2;
    cgstAmount = (taxableAmount * cgstPercent) / 100;
    sgstAmount = (taxableAmount * sgstPercent) / 100;
  }

  const gstAmount = cgstAmount + sgstAmount + igstAmount;
  const totalAmount = taxableAmount + gstAmount;

  return {
    subtotal, discountAmount, discountPercent, taxableAmount, gstPercent: gst, gstAmount,
    cgstPercent, cgstAmount, sgstPercent, sgstAmount, igstPercent, igstAmount,
    totalAmount, isInterState,
  };
};

let _currency = { code: 'INR', symbol: '₹', locale: 'en-IN' };

export const setCurrencyConfig = (cfg) => {
  if (cfg?.code) _currency = cfg;
};

export const getCurrencySymbol = () => _currency.symbol;

export const ENUMS = {
  LEAD_SOURCES: ['Meta Ads', 'Google Ads', 'Website', 'WhatsApp', 'Reference', 'Telecalling', 'Social Media', 'CSV Import', 'Instagram DM', 'Quotation', 'Justdial', 'Invoice', 'Walk-in', 'Cold visit', 'Other'],
  LEAD_STATUSES: ['New', 'Follow-up', 'Meeting Fixed', 'Meeting Done', 'Quotation', 'Review', 'Won', 'Lost', 'Repeated'],
  LEAD_PRIORITIES: ['Hot', 'Warm', 'Cold'],
  CALL_STATUSES: ['Answered', 'Not Answered', 'Not Reachable', 'Number Not Working'],

  APPOINTMENT_TYPES: ['Call', 'Meeting', 'Demo', 'Site Visit', 'Follow-up', 'Other'],
  APPOINTMENT_STATUSES: ['Scheduled', 'Completed', 'Cancelled', 'No Show'],
  CONTENT_PLATFORMS: ['Instagram', 'Facebook', 'LinkedIn', 'YouTube', 'Twitter/X', 'Website', 'Google', 'WhatsApp', 'Other'],
  CONTENT_TYPES: ['Post', 'Story', 'Reel', 'Video', 'Blog', 'Ad Creative', 'Banner', 'Logo', 'Brochure', 'Other'],
  CONTENT_STATUSES: ['Pending', 'In Progress', 'Review', 'Approved', 'Published', 'Rejected'],
  PAYMENT_MODES: ['UPI', 'Bank Transfer', 'Cash', 'Cheque', 'Online', 'Card'],
  ROLES: ['employee', 'admin'],
  PLANS: ['trial', 'starter', 'growth', 'agency', 'custom'],
};

// Whole-number amounts stay clean (₹300), but anything with paise/cents keeps its
// full precision (₹599.40) instead of silently dropping it — a table of amounts
// that each round off individually will otherwise never reconcile with a total
// computed server-side from the exact decimal values. The minor-unit digit count
// (2 for INR/USD, 0 for JPY, 3 for KWD/BHD/OMR, etc.) comes from Intl itself
// rather than a hardcoded table, so this stays correct for every currency in
// CURRENCIES above without listing them one by one. The locale (e.g. 'en-IN')
// still drives digit grouping, so INR keeps the lakh/crore system regardless.
export const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return `${_currency.symbol}0`;
  const num = parseFloat(amount);
  if (isNaN(num)) return `${_currency.symbol}0`;
  try {
    const nativeDigits = new Intl.NumberFormat(_currency.locale, { style: 'currency', currency: _currency.code })
      .resolvedOptions().maximumFractionDigits;
    const scale = 10 ** nativeDigits;
    const hasFraction = Math.round(num * scale) % scale !== 0;
    const digits = hasFraction ? nativeDigits : 0;
    return new Intl.NumberFormat(_currency.locale, { style: 'currency', currency: _currency.code, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(num);
  } catch {
    return `${_currency.symbol}${num.toLocaleString()}`;
  }
};

export const formatDate = (date) => {
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }).format(new Date(date));
};

export const formatDateTime = (date) => {
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }).format(new Date(date));
};

export const formatTime = (date) => {
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }).format(new Date(date));
};

export const timeAgo = (date) => {
  if (!date) return '';
  const seconds = Math.floor((Date.now() - new Date(date)) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return formatDate(date);
};

export const getStatusColor = (status) => {
  const map = {
    New: '#0ea5e9', 'Follow-up': '#f59e0b', 'Meeting Fixed': '#7c3aed', 'Meeting Done': '#8b5cf6',
    Quotation: '#e94560', Review: '#f97316', Won: '#22c55e', Lost: '#6b7280', Repeated: '#06b6d4',
    pending: '#f59e0b', completed: '#22c55e', overdue: '#ef4444', cancelled: '#6b7280',
    Scheduled: '#0ea5e9', Completed: '#22c55e', Cancelled: '#6b7280', 'No Show': '#ef4444',
    Draft: '#6b7280', Sent: '#0ea5e9', Approved: '#22c55e', Rejected: '#ef4444',
    Unpaid: '#ef4444', Partial: '#f59e0b', Paid: '#22c55e', Overdue: '#e94560',
    Pending: '#f59e0b', 'In Progress': '#0ea5e9', 'In Review': '#7c3aed', Published: '#22c55e',
    trial: '#6b7280', starter: '#0ea5e9', growth: '#7c3aed', agency: '#e94560', custom: '#22c55e',
    Answered: '#22c55e', 'Not Answered': '#f59e0b', 'Not Reachable': '#ef4444', 'Number Not Working': '#6b7280',
  };
  return map[status] || '#6b7280';
};

export const getPriorityColor = (priority) => {
  const map = { Hot: '#22c55e', Warm: '#f59e0b', Cold: '#ef4444', Low: '#6b7280', Medium: '#0ea5e9', High: '#f59e0b' };
  return map[priority] || '#6b7280';
};

export const getPriorityBg = (priority) => {
  const map = { Hot: 'rgba(34,197,94,0.15)', Warm: 'rgba(245,158,11,0.15)', Cold: 'rgba(239,68,68,0.15)', Low: 'rgba(107,114,128,0.15)', Medium: 'rgba(14,165,233,0.15)', High: 'rgba(245,158,11,0.15)' };
  return map[priority] || 'rgba(107,114,128,0.15)';
};

export const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
};

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const debounce = (fn, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
};

export const truncate = (str, len = 50) => {
  if (!str) return '';
  return str.length > len ? str.slice(0, len) + '...' : str;
};

export const getMonthName = (month) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[(parseInt(month) - 1) % 12] || '';
};

export const buildMonthlyChartData = (data, months = 12) => {
  const now = new Date();
  const labels = [];
  const values = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    labels.push(getMonthName(d.getMonth() + 1) + ' ' + d.getFullYear());
    const found = data.find((r) => parseInt(r.month) === d.getMonth() + 1 && parseInt(r.year) === d.getFullYear());
    values.push(found ? parseFloat(found.total || found.count || 0) : 0);
  }
  return { labels, values };
};

// --- Future-date guards for scheduling inputs (appointments, followups, due dates, etc.) ---
// Not for filter/report date-range pickers, which need to select past dates on purpose.

const pad2 = (n) => String(n).padStart(2, '0');

export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export const nowLocalISO = () => {
  const d = new Date();
  return `${todayISO()}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

export const isPastDate = (dateStr) => !!dateStr && dateStr < todayISO();

export const isPastDateTime = (dtStr) => !!dtStr && dtStr < nowLocalISO();

export const isPastTimeToday = (dateStr, timeStr) => {
  if (!timeStr) return false;
  // No date picked yet? The earliest legal date is always today (dates
  // before today are already blocked elsewhere), so treat an empty date
  // the same as today rather than skipping the check — otherwise picking
  // a past time before picking the date lets it through unvalidated.
  const effectiveDate = dateStr || todayISO();
  if (effectiveDate !== todayISO()) return false;
  return timeStr < nowLocalISO().slice(11, 16);
};

export const validateFutureDate = (value, label = 'Date') => {
  if (isPastDate(value)) {
    toast.error(`${label} cannot be in the past`);
    return false;
  }
  return true;
};

export const validateFutureDateTime = (value, label = 'Date & time') => {
  if (isPastDateTime(value)) {
    toast.error(`${label} cannot be in the past`);
    return false;
  }
  return true;
};

export const validateFutureTime = (dateStr, timeStr, label = 'Time') => {
  if (isPastTimeToday(dateStr, timeStr)) {
    toast.error(`${label} cannot be in the past for today's date`);
    return false;
  }
  return true;
};
